import {Grid} from 'src/design/atoms/Grid';
import {Stack} from 'src/design/atoms/Stack';
import React, {useMemo} from 'react';
import {FormReferenceImage} from 'src/app/forms/FormReferenceImage';
import type {IFormFieldV2} from 'src/types/FormV2';
import {FormAssetSelectorV2} from './FormAssetSelector/FormAssetSelectorV2';
import {FormAutocompleteV2} from './FormAutocomplete/FormAutocompleteV2';
import {FormDatePickerV2} from './FormDatePicker/FormDatePickerV2';
import type {DatePreset} from 'src/design/atoms/datePresets';
import {getDefaultDatePresets} from 'src/design/atoms/datePresets';
import {FormDrawingPadV2} from './FormDrawingPad/FormDrawingPadV2';
import {FormFileUploadV2} from './FormFileDropzone/FormFileUploadV2';
import {FormMeasurementFieldV2} from './FormMeasurementField/FormMeasurementFieldV2';
import {FormNumberV2} from './FormNumber/FormNumberV2';
import {FormProjectSelectorV2} from './FormProjectSelector/FormProjectSelectorV2';
import {FormPersonSelectorV2} from './FormPersonSelector/FormPersonSelectorV2';
import {FormPersonnelSelectModalV2} from './FormPersonnelSelectModal/FormPersonnelSelectModalV2';
import {FormPhoneNumberV2} from './FormPhoneNumber/FormPhoneNumberV2';
import {FormRichTextEditorV2} from './FormRichTextEditor/FormRichTextEditorV2';
import {FormScaleV2} from './FormScale/FormScaleV2';
import {FormSelectV2} from './FormSelectBox/FormSelectV2';
import {FormSelectModalV2} from './FormSelectModal/FormSelectModalV2';
import {FormSignatureV2} from './FormSignature/FormSignatureV2';
import {FormDividerV2} from './FormStatic/FormDividerV2';
import {FormHeadingV2} from './FormStatic/FormHeadingV2';
import {FormParagraphV2} from './FormStatic/FormParagraphV2';
import {FormSubformV2} from './FormSubform/FormSubformV2';
import {FormSwitchV2} from './FormSwitch/FormSwitchV2';
import {FormTagsInputV2} from './FormTagsInput/FormTagsInputV2';
import {FormTextAreaV2} from './FormTextArea/FormTextAreaV2';
import {FormTextBoxV2} from './FormTextBox/FormTextBoxV2';
import {FormVideoSelector} from './FormVideoSelector';
import {FormViewField} from './FormViewField';
import {useFieldGridSpan} from './engine/useFieldGridSpan';

const DEFAULT_DATE_PRESETS: DatePreset[] = getDefaultDatePresets();

export interface FormFieldProps {
  field: IFormFieldV2;
  value: any;
  error?: string;
  onChange: (name: string, value: any, displayValue?: string) => void;
}

/**
 * FormField renders the appropriate field component based on the field type.
 */
export const FormField = React.memo<FormFieldProps>(
  ({field, value, error, onChange}) => {
    // Standardize values for consistent handling
    const textValue = value || '';
    const numberValue = field.type === 'number' ? value ?? '' : value;

    // Memoize the field component
    const fieldComponent = useMemo(() => {
      switch (field.type) {
        case 'text':
          if (field.textboxOptions?.multiline !== true) {
            return (
              <FormTextBoxV2
                field={field}
                value={textValue}
                error={error}
                onChange={onChange}
              />
            );
          } else {
            return (
              <FormTextAreaV2
                field={field}
                value={textValue}
                error={error}
                onChange={onChange}
              />
            );
          }

        case 'textarea':
          return (
            <FormTextAreaV2
              field={field}
              value={textValue}
              error={error}
              onChange={onChange}
            />
          );
        case 'autocomplete':
          return (
            <FormAutocompleteV2
              field={field}
              value={textValue}
              error={error}
              onChange={onChange}
            />
          );
        case 'select':
          return (
            <FormSelectV2
              field={field}
              value={textValue}
              error={error}
              onChange={onChange}
            />
          );
        case 'select-multiple':
          return (
            <FormSelectModalV2
              field={field}
              value={value}
              error={error}
              onChange={onChange}
            />
          );
        case 'number':
          return (
            <FormNumberV2
              field={field}
              value={numberValue}
              error={error}
              onChange={onChange}
            />
          );
        case 'date':
          return (
            <FormDatePickerV2
              field={field}
              value={value}
              error={error}
              onChange={onChange}
              presets={DEFAULT_DATE_PRESETS}
            />
          );
        case 'switch':
          return (
            <FormSwitchV2
              field={field}
              value={value}
              error={error}
              onChange={onChange}
            />
          );
        case 'asset':
          return (
            <FormAssetSelectorV2
              field={field}
              value={textValue}
              error={error}
              onChange={onChange}
            />
          );
        case 'person':
          return (
            <FormPersonSelectorV2
              field={field}
              value={textValue}
              error={error}
              onChange={onChange}
            />
          );
        case 'project':
          return (
            <FormProjectSelectorV2
              field={field}
              value={value}
              error={error}
              onChange={onChange}
            />
          );
        case 'person-select-multiple':
          return (
            <FormPersonnelSelectModalV2
              field={field}
              value={textValue}
              error={error}
              onChange={onChange}
            />
          );
        case 'video-select':
          return (
            <FormVideoSelector
              field={field}
              value={textValue}
              error={error}
              onChange={onChange}
            />
          );
        case 'richtext':
          return (
            <FormRichTextEditorV2
              field={field}
              value={textValue}
              error={error}
              onChange={onChange}
            />
          );
        case 'file':
          return (
            <FormFileUploadV2
              field={field}
              value={value || null}
              error={error}
              onChange={(name, value) => onChange(name, value)}
            />
          );
        case 'scale':
          return (
            <FormScaleV2
              field={field}
              value={value}
              error={error}
              onChange={onChange}
            />
          );
        case 'phone':
          return (
            <FormPhoneNumberV2
              field={field}
              value={textValue}
              error={error}
              onChange={onChange}
            />
          );
        case 'signature':
          return (
            <FormSignatureV2
              field={field}
              value={textValue}
              error={error}
              onChange={onChange}
            />
          );
        case 'drawing-pad':
          return (
            <FormDrawingPadV2
              field={field}
              value={textValue}
              error={error}
              onChange={onChange}
            />
          );
        case 'paragraph':
          return <FormParagraphV2 field={field} />;
        case 'heading':
          return <FormHeadingV2 field={field} />;
        case 'divider':
          return <FormDividerV2 field={field} />;
        case 'subform':
          return <FormSubformV2 field={field} />;
        case 'measurement':
          return (
            <FormMeasurementFieldV2
              field={field}
              value={value}
              error={error}
              onChange={onChange}
            />
          );
        case 'tags':
          return (
            <FormTagsInputV2
              field={field}
              value={Array.isArray(value) ? value : []}
              error={error}
              onChange={onChange}
            />
          );
        default:
          return null;
      }
    }, [field, textValue, numberValue, value, error, onChange]);

    const span = useFieldGridSpan(field.size);

    // If field is readOnly, render the view version instead
    if (field.readOnly) {
      return (
        <Grid.Col id={`form-field-${field.name}`} span={span}>
          <FormViewField field={field} value={value} />
        </Grid.Col>
      );
    }

    return (
      <Grid.Col id={`form-field-${field.name}`} span={span}>
        <Stack gap="sm">
          {fieldComponent}
          <FormReferenceImage field={field} />
        </Stack>
      </Grid.Col>
    );
  }
);
